/**
 * Shared helpers for payments tabs — remaining/paid math, auto-match heuristics.
 */

import type { components } from "@/lib/api/generated/schema";

export type Order = components["schemas"]["Order"];
export type Payment = components["schemas"]["Payment"];

type BillableOrder = {
  total?: number | null;
  manualDiscount?: number | null;
  promoDiscount?: number | null;
  costumeExtra?: number | null;
  payableTotal?: number | null;
  // Pre-summed surcharge total carried on the light OrderSummary payload.
  surchargesTotal?: number | null;
  // JSONB mirror of order_surcharges carried on the full Order payload. Typed
  // as unknown in the generated schema, so parse defensively.
  surcharges?: unknown;
};

type PaymentBalanceOrder = BillableOrder & {
  deposit?: number | null;
  // Ảnh bill cọc của ĐƠN CŨ. Đơn mới đính ảnh vào chính phiếu thu cọc, xem
  // paymentBalance.
  depositBillImage?: string | null;
};

/**
 * Σ phiếu ĐÃ XÁC NHẬN khách trả VÀO — bỏ phiếu hoàn (`paymentType === "refund"`,
 * số ÂM).
 *
 * Khoản hoàn không sinh công nợ (lỗi #01 kiểm thử toàn vai 16/09/2026): cộng
 * dòng âm vào đây thì khách trả đủ 594.000đ, được hoàn 200.000đ là đơn hiện
 * "còn phải thu 200.000đ". Tiền đã hoàn xem `confirmedRefunds`.
 */
export function confirmedPaid(payments: Payment[] | undefined): number {
  if (!payments) return 0;
  return payments
    .filter((p) => p.status === "confirmed" && p.paymentType !== "refund")
    .reduce((s, p) => s + (p.amount ?? 0), 0);
}

/** Σ phiếu hoàn ĐÃ duyệt chi của đơn, số DƯƠNG — cùng nghĩa `order.refunded`. */
export function confirmedRefunds(payments: Payment[] | undefined): number {
  if (!payments) return 0;
  return payments
    .filter((p) => p.status === "confirmed" && p.paymentType === "refund")
    .reduce((s, p) => s - (p.amount ?? 0), 0);
}

/**
 * Σ phiếu CHỜ XÁC NHẬN mà tiệm đã CẦM tiền — nhân viên ghi nhận, đợi kế toán.
 *
 * Bỏ dòng hoàn tiền (`paymentType === "refund"`, số ÂM) đúng như cột `pending`
 * của máy chủ (payment/repo.go `balanceColumnsSQL`). Cộng số âm đó vào đây làm
 * "còn phải thu" PHÌNH lên đúng bằng khoản sắp trả lại khách, tức trần của form
 * thu tiền rộng hơn trần máy chủ — người dùng gõ vừa mắt rồi ăn 400 lúc gửi.
 *
 * Bỏ cả phiếu `qrWaiting` (mã QR khách CHƯA chuyển): trừ lời mời vào "còn lại"
 * là màn ghi "CÒN LẠI 0đ" và mã trả nốt thu thiếu đúng số đó, còn cửa tải ảnh
 * khoá khách (khách Hồng Tuyên 10/10/2026, CEO chốt 1a 11/10/2026).
 */
export function pendingPaid(payments: Payment[] | undefined): number {
  if (!payments) return 0;
  return payments
    .filter((p) => p.status === "pending" && p.paymentType !== "refund" && !p.qrWaiting)
    .reduce((s, p) => s + (p.amount ?? 0), 0);
}

/**
 * Σ mã QR khách CHƯA chuyển (`qrWaiting`) — lời mời, chưa phải tiền. Không trừ
 * vào "còn lại"; chỉ để màn nói được "đơn đang có mã chờ khách quét".
 */
export function qrWaitingPaid(payments: Payment[] | undefined): number {
  if (!payments) return 0;
  return payments
    .filter((p) => p.status === "pending" && p.paymentType !== "refund" && !!p.qrWaiting)
    .reduce((s, p) => s + (p.amount ?? 0), 0);
}

/**
 * Dòng phát sinh có được tính vào tiền phải thu không (spec M1 17/08/2026).
 * Dòng pending/rejected không phải tiền phải thu; mirror cũ không có status
 * (hoặc status rỗng) → coi như active (backfill bao dung). Dùng `||` chứ
 * KHÔNG `??` — chuỗi rỗng cũng phải coi là active.
 */
export function isBillableSurcharge(s: { status?: string | null }): boolean {
  return (s.status || "active") === "active";
}

/** Một dòng phát sinh còn hiệu lực, đủ dữ liệu để in cho khách xem. */
export type BillableSurchargeLine = {
  id?: string | undefined;
  label: string;
  amount: number;
  qty: number;
  /** VAT riêng của dòng — mirror hiện chưa ghi (HRS-053 sẽ thêm); vắng = 0. */
  vatAmount: number;
};

/**
 * Liệt kê các dòng phát sinh ĐƯỢC TÍNH TIỀN của một đơn, theo đúng bộ lọc mà
 * sumOrderSurcharges dùng. Bill nhiệt và bảng thông tin đơn in danh sách này
 * ngay trên dòng Tổng thanh toán, nên tổng các dòng phải khớp con số đã cộng
 * vào tổng — vì vậy dòng amount hỏng (không phải số) bị BỎ HẲN, y như phép
 * cộng bỏ nó. Payload chỉ có scalar `surchargesTotal` (OrderSummary, không
 * kèm mảng) → trả rỗng: không có gì để liệt kê, tổng vẫn đúng nhờ scalar.
 */
export function listBillableSurcharges(o: BillableOrder): BillableSurchargeLine[] {
  const raw = o.surcharges;
  if (!Array.isArray(raw)) return [];
  const lines: BillableSurchargeLine[] = [];
  for (const entry of raw) {
    const e = entry as
      | { id?: unknown; label?: unknown; amount?: unknown; qty?: unknown; vatAmount?: unknown; status?: unknown }
      | null
      | undefined;
    if (!e) continue;
    if (!isBillableSurcharge({ status: typeof e.status === "string" ? e.status : null })) continue;
    if (typeof e.amount !== "number" || !Number.isFinite(e.amount)) continue;
    lines.push({
      id: typeof e.id === "string" ? e.id : undefined,
      label: typeof e.label === "string" && e.label.trim() !== "" ? e.label : "Phát sinh",
      amount: e.amount,
      qty: typeof e.qty === "number" && Number.isFinite(e.qty) && e.qty > 0 ? e.qty : 1,
      vatAmount: typeof e.vatAmount === "number" && Number.isFinite(e.vatAmount) ? e.vatAmount : 0,
    });
  }
  return lines;
}

/**
 * Sum of an order's surcharges. Surcharges are billed on top of payable_total
 * (e.g. CSKH extra-photo fees). Prefers the pre-summed `surchargesTotal`
 * scalar (OrderSummary payload — BE already sums only status='active' rows);
 * falls back to the parsed `surcharges` JSONB array (full Order payload) via
 * listBillableSurcharges, so the printed line items can never drift from the
 * amount added to the total. Cộng cả vatAmount từng dòng — nhánh HRS-053 định
 * nghĩa tiền phải thu phía BE là SUM(s.amount + s.vat_amount); mirror chưa có
 * field thì vatAmount = 0, không đổi gì.
 */
export function sumOrderSurcharges(o: BillableOrder): number {
  if (typeof o.surchargesTotal === "number" && Number.isFinite(o.surchargesTotal)) {
    return o.surchargesTotal;
  }
  return listBillableSurcharges(o).reduce((s, l) => s + l.amount + l.vatAmount, 0);
}

/**
 * Riêng phần VAT của các dòng phát sinh billable (HRS-053) — cho màn hình muốn
 * tách "dòng VAT" (VAT gói + VAT phát sinh) khỏi tiền hàng. Đi qua đúng
 * listBillableSurcharges như sumOrderSurcharges, nên dòng nào được cộng vào
 * tổng thì phần thuế của nó cũng được tách ra ở đây — không có đường thứ hai
 * để hai con số trôi khỏi nhau. Payload OrderSummary chỉ có scalar đã gộp
 * (không kèm mảng) thì không tách được → trả 0.
 */
export function sumOrderSurchargesVAT(o: Pick<BillableOrder, "surcharges">): number {
  return listBillableSurcharges(o).reduce((s, l) => s + l.vatAmount, 0);
}

/**
 * Đơn có dòng phát sinh loại GÓI đang tính tiền không — khách đã mua thêm set
 * thứ 2 (QĐ 0123/0125). Nuôi điều kiện cùng tên của mã ưu đãi ở màn SỬA đơn,
 * nơi chỉ có mirror `orders.surcharges` chứ không có bảng phát sinh.
 *
 * Nhà ở đây vì đây là chỗ ở của mọi phép đọc phòng thủ trên mirror ấy.
 *
 * ⚠️ CỐ Ý KHÔNG dùng lại `isBillableSurcharge`: hàm đó coi status VẮNG MẶT là
 * "active" (bao dung với dòng cũ trước khi có cột status) — nới. Ở đây phải
 * chặt: status không nói rõ thì KHÔNG tính là đã mua set thứ 2. Ẩn oan một mã
 * là chuyện người bán sửa trong một giây; hứa số giảm rồi bị máy chủ từ chối
 * sau khi đã nói giá với khách thì không.
 *
 * Mirror KHÔNG mang `catalogId` (xem SurchargeMirrorEntry phía máy chủ), nên
 * chỉ nhận diện qua `type` — máy chủ nhận CẢ hai tín hiệu nên nó luôn lỏng hơn
 * hoặc bằng chỗ này, đúng chiều an toàn.
 */
export function hasActivePackageSurcharge(o: Pick<BillableOrder, "surcharges">): boolean {
  const raw = o.surcharges;
  if (!Array.isArray(raw)) return false;
  return raw.some((entry) => {
    const e = entry as { type?: unknown; status?: unknown } | null | undefined;
    return e?.type === "package" && e?.status === "active";
  });
}

/**
 * Total to collect. New orders snapshot gross payable total including VAT;
 * surcharges (extra-photo fees, costume upcharges, etc.) are added on top of
 * that snapshot — mirrors the BE billable definition in
 * payment/repo.go `OrderPaymentBalanceInTx`.
 */
export function billableTotal(o: BillableOrder): number {
  const surcharges = sumOrderSurcharges(o);
  if (typeof o.payableTotal === "number" && o.payableTotal > 0) {
    return o.payableTotal + surcharges;
  }
  const total = o.total ?? 0;
  const md = o.manualDiscount ?? 0;
  const pd = o.promoDiscount ?? 0;
  const cx = o.costumeExtra ?? 0;
  return Math.max(0, total - md - pd + cx) + surcharges;
}

/**
 * Tiền khách đã trả VÀO một đơn, đọc từ chính bản ghi đơn (không cần danh sách
 * phiếu): cọc đơn cũ + `paid` (đã trừ khoản hoàn) + `refunded` (cộng lại khoản
 * hoàn). Dùng ở các màn chỉ có dữ liệu đơn — danh sách, lịch ê-kíp, khung chat.
 */
export function orderReceived(o: {
  deposit?: number | null;
  paid?: number | null;
  refunded?: number | null;
}): number {
  return (o.deposit ?? 0) + (o.paid ?? 0) + (o.refunded ?? 0);
}

/**
 * Còn phải thu đọc từ bản ghi đơn: phải thu − tiền khách đã trả vào, chặn ở 0.
 * Khoản hoàn không sinh công nợ — cùng luật máy chủ
 * `payment.OrderPaymentBalance.RemainingConfirmed`.
 */
export function orderRemaining(
  o: BillableOrder & { deposit?: number | null; paid?: number | null; refunded?: number | null },
): number {
  return Math.max(0, billableTotal(o) - orderReceived(o));
}

/**
 * Remaining amount = billable - deposit - confirmedPaid - pendingPaid.
 *
 * `order.deposit` is money the customer already paid at booking (tracked as an
 * order field, not a payment record), so it must be subtracted alongside the
 * payment collection. Mirrors the old PaymentPage `effectivePaid = deposit +
 * payments`. Pending payments are treated as committed. Refund rows are left
 * out of both sums: a refund is never a debt.
 */
export function remainingAmount(o: PaymentBalanceOrder, payments: Payment[] | undefined): number {
  const bill = billableTotal(o);
  const deposit = o.deposit ?? 0;
  return Math.max(0, bill - deposit - confirmedPaid(payments) - pendingPaid(payments));
}

/** Người nhận ghi trên phiếu khách tự mở mã QR trên Cổng (máy chủ: `ReceivedByCustomerPortal`). */
export const RECEIVED_BY_CUSTOMER_PORTAL = "customer_portal";

/**
 * Phiếu nhân viên cùng số đang là LỜI MỜI khách chuyển (đứng sau mã QR còn sống,
 * chưa nối khoản ngân hàng nào) — tiền về đúng mã là tự vào phiếu đó. Mã Cổng
 * khách và phiếu đơn in đứng ngoài: máy chủ xử chúng theo đường riêng (QĐ 0350).
 */
export function pendingQrWaitingSameAmount(
  payments: Payment[] | undefined,
  amount: number,
): Payment | undefined {
  return (payments ?? []).find(
    (p) =>
      p.status === "pending" &&
      p.qrWaiting &&
      !p.bankTxnId &&
      p.paymentType !== "refund" &&
      p.amount === amount &&
      !p.printOrderTicket &&
      !isOpenPortalHold(p),
  );
}

/**
 * Phiếu là mã QR khách mở dở trên Cổng: phiếu Cổng còn chờ và CHƯA có giao dịch
 * ngân hàng nào về. Cùng vị từ với máy chủ (`CancelBalancePortalIntentsForOrderInTx`):
 * nhân viên ghi phiếu thu là máy chủ đóng các phiếu này trong cùng lần ghi.
 * Phiếu Cổng đã dính giao dịch ngân hàng là tiền đã về — không thuộc tập này.
 * Phiếu đang gắn đơn in (`printOrderTicket`) cũng không: mã đó chỉ mang tiền
 * hàng in, máy chủ chừa nó ra (QĐ 0350).
 */
export function isOpenPortalHold(p: Payment): boolean {
  return (
    p.status === "pending" &&
    p.receivedBy === RECEIVED_BY_CUSTOMER_PORTAL &&
    !p.bankTxnId &&
    !p.printOrderTicket &&
    p.paymentType !== "refund"
  );
}

/**
 * Trần của phiếu NHÂN VIÊN sắp ghi — `remainingAmount` cộng lại phần mã QR Cổng
 * dở đang giữ, vì máy chủ nhả phần đó ngay trong lần ghi (đơn AT-2610-245,
 * 10/10/2026: mã 48.600đ giữ chỗ, lễ tân thu thiếu đúng khoản phát sinh đó;
 * QĐ 0350).
 * Chỉ dùng cho form/nút ghi phiếu thu; các con số hiển thị vẫn đọc `remainingAmount`.
 */
export function staffCollectableAmount(
  o: PaymentBalanceOrder,
  payments: Payment[] | undefined,
): number {
  return remainingAmount(o, (payments ?? []).filter((p) => !isOpenPortalHold(p)));
}

export function paymentBalance(o: PaymentBalanceOrder, payments: Payment[] | undefined) {
  const bill = billableTotal(o);
  const deposit = o.deposit ?? 0;
  const paid = confirmedPaid(payments);
  const refunded = confirmedRefunds(payments);
  const pending = pendingPaid(payments);
  const qrWaiting = qrWaitingPaid(payments);
  // Cọc nằm ở HAI NƠI tuỳ tuổi của đơn (quyết định 0087, 02/09/2026): đơn cũ
  // giữ số ở cột `orders.deposit`; đơn mới có `deposit = 0` và khoản cọc là
  // một PHIẾU THU loại `deposit`. Cộng cả hai KHÔNG đếm trùng vì không đơn nào
  // có tiền ở cả hai chỗ — nhưng chỉ đọc `o.deposit` thì cột Cọc của mọi đơn
  // mới hiện dấu gạch, trong khi khách đã trả tiền thật.
  //
  // Chỉ để HIỂN THỊ. `remaining` bên dưới vẫn trừ `deposit` (đơn cũ) và
  // `paid` (đơn mới) riêng — cộng depositTotal vào đó là trừ hai lần.
  const depositReceipts = (payments ?? []).filter(
    (p) => p.paymentType === "deposit" && p.status === "confirmed" && p.amount > 0,
  );
  const depositTotal = deposit + depositReceipts.reduce((sum, p) => sum + p.amount, 0);
  return {
    bill,
    deposit,
    depositTotal,
    depositBillImage: o.depositBillImage || depositReceipts.find((p) => p.billImage)?.billImage,
    paid,
    // Đã hoàn cho khách — chỉ để HIỂN THỊ, không trừ vào `remaining`.
    refunded,
    pending,
    // Mã QR khách chưa chuyển — KHÔNG trừ vào `remaining` (CEO chốt 1a 11/10/2026).
    qrWaiting,
    remaining: Math.max(0, bill - deposit - paid - pending),
  };
}

/**
 * Khối tiền của trang chi tiết đơn — MỘT phép tính cho cả bản máy tính
 * (OrderMoneyPanel) lẫn bản điện thoại (OrderInfoRows), để hai màn không bao
 * giờ nói hai con số.
 *
 * - `collected`: tiền khách đã trả VÀO (cọc + phiếu đã xác nhận), không trừ
 *   khoản đã hoàn — khoản hoàn không sinh công nợ (lỗi #01 kiểm thử 16/09/2026).
 * - `depositShown`: cọc hiển thị. Có danh sách phiếu thì gồm cả phiếu thu loại
 *   `deposit` — đơn mới ghi `deposit = 0`, chỉ đọc bản ghi đơn là "Đặt cọc −0đ"
 *   dù khách đã cọc.
 * - Phiếu chờ xác nhận KHÔNG trừ ở đây (khác `remainingAmount` của hộp thu tiền).
 *
 * Chưa có danh sách phiếu (đang tải) thì đọc bản ghi đơn.
 */
export function orderMoneySummary(o: Order, payments: Payment[] | undefined) {
  const payableTotal = billableTotal(o);
  const bal = payments ? paymentBalance(o, payments) : null;
  const collected = bal ? bal.deposit + bal.paid : orderReceived(o);
  const depositShown = bal ? bal.depositTotal : (o.deposit ?? 0);
  return {
    payableTotal,
    collected,
    depositShown,
    paidAfterDeposit: Math.max(0, collected - depositShown),
    refunded: bal ? bal.refunded : (o.refunded ?? 0),
    remaining: Math.max(0, payableTotal - collected),
  };
}

/** Try to extract an order code from a bank txn memo (e.g. "GN12345" or last 6 hex). */
export function guessOrderCode(memo: string | null | undefined): string | null {
  if (!memo) return null;
  const m1 = memo.match(/(GN[\dA-Za-z]{4,12})/i);
  if (m1) return m1[1]!.toUpperCase();
  const m2 = memo.match(/\b([0-9A-Fa-f]{6})\b/);
  if (m2) return m2[1]!.toUpperCase();
  return null;
}

/**
 * Auto-match score 0..100. Combines:
 *   amount equal (50)
 *   memo contains last-6 of payment.id (30)
 *   memo contains last-6 of orderId  (20)
 */
export function matchScore(
  txAmount: number,
  txContent: string | null | undefined,
  payment: Payment,
): number {
  let s = 0;
  if (txAmount === payment.amount) s += 50;
  const memo = (txContent ?? "").toUpperCase();
  if (memo.length > 0) {
    const pTail = payment.id.replace(/-/g, "").slice(-6).toUpperCase();
    if (memo.includes(pTail)) s += 30;
    if (payment.orderId) {
      const oTail = payment.orderId.replace(/-/g, "").slice(-6).toUpperCase();
      if (memo.includes(oTail)) s += 20;
    }
  }
  return s;
}
